/** The roster module's reads and writes of the API: TanStack Query hooks, and a few plain calls for a form to make as it is filled in. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPatch, apiPost } from '../core/index.js'
import type { CreateWorkerInput, RetentionReport, UpdateWorkerInput, WorkerProfile, WorkerSearchResults, WorkerTableRow } from './types.js'

/** The facility's hires and leavers month by month, for the Reporting tab's Retention card. */
export function useRetentionReport(facilityId: string, months: number) {
  return useQuery({
    queryKey: ['retention-report', facilityId, months],
    queryFn: () => apiGet<RetentionReport>(`/facilities/${facilityId}/reports/retention?months=${months}`),
    placeholderData: keepPreviousData,
  })
}

export function useWorkerRoster(facilityId: string) {
  return useQuery({
    queryKey: ['workers', facilityId],
    queryFn: () => apiGet<WorkerTableRow[]>(`/facilities/${facilityId}/workers`),
  })
}

/**
 * Worker-scoped reads.
 *
 * Each takes the worker to load, or null to stay idle - the operator
 * dashboard only fetches a worker's detail once their row is expanded,
 * rather than loading every worker on the roster up front. The worker app
 * passes its own id from `GET /auth/me`; an operator passes whichever row
 * is open.
 */
export function useWorkerProfile(workerId: string | null) {
  return useQuery({
    queryKey: ['worker-profile', workerId],
    queryFn: () => apiGet<WorkerProfile>(`/workers/${workerId as string}`),
    enabled: workerId !== null,
  })
}

/**
 * Worker search for the signed-in session. The API scopes it from the token
 * (operator: every facility of the client; facility administrator: only the
 * ones they manage), so there is no scope to pass. Idle until the query is
 * long enough for the API to accept.
 */
export function useWorkerSearch(query: string) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: ['worker-search', trimmed],
    queryFn: () => apiGet<WorkerSearchResults>(`/workers?q=${encodeURIComponent(trimmed)}`),
    enabled: trimmed.length >= 2,
    placeholderData: keepPreviousData,
  })
}

/** An operator adding one worker to a facility's roster. Refreshes the roster, the facility counts and the dashboard figures. */
export function useCreateWorker(clientId: string | undefined, facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateWorkerInput) =>
      apiPost<WorkerProfile>(`/admin/clients/${encodeURIComponent(clientId ?? '')}/facilities/${encodeURIComponent(facilityId)}/workers`, input),
    onSuccess: () => {
      // A new worker's hire date changes the retention report too.
      // The month's health weighs the roster's FTEs against the plan, so a new worker changes its warnings.
      for (const key of ['workers', 'dashboard-summary', 'tier-distribution', 'retention-report', 'month-health']) {
        void queryClient.invalidateQueries({ queryKey: [key, facilityId] })
      }
      void queryClient.invalidateQueries({ queryKey: ['facilities'] })
    },
  })
}

/** An operator editing a worker's name, role, hire date or readings, through the same endpoint the admin uses. Refreshes the roster and the worker's own reads. */
export function useUpdateWorker(clientId: string | undefined, facilityId: string, workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateWorkerInput) =>
      apiPatch<WorkerProfile>(
        `/admin/clients/${encodeURIComponent(clientId ?? '')}/facilities/${encodeURIComponent(facilityId)}/workers/${encodeURIComponent(workerId)}`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workers', facilityId] })
      // The hire and leave dates are what the retention report counts.
      void queryClient.invalidateQueries({ queryKey: ['retention-report', facilityId] })
      // A role, employment type, contracted hours or credential changes the month's health.
      void queryClient.invalidateQueries({ queryKey: ['month-health', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['worker-profile', workerId] })
      void queryClient.invalidateQueries({ queryKey: ['worker-search'] })
      // The Clinical Bridge eligibility lives on the worker's Bridge record.
      void queryClient.invalidateQueries({ queryKey: ['clinical-bridge', workerId] })
    },
  })
}
